import React, { useState, useRef, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Database,
  Download,
  Upload,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Shield,
  HardDrive,
  Trash2,
  CalendarDays,
  CalendarRange,
  Hand,
  LifeBuoy,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import api from "@/api";

function formatSize(bytes) {
  if (bytes == null) return "—";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / 1048576).toFixed(2) + " MB";
}

function formatDate(isoStr) {
  if (!isoStr) return "—";
  try {
    const d = new Date(isoStr);
    return d.toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return isoStr; }
}

const KIND_META = {
  daily:     { label: "Giornaliero", color: "bg-blue-100 text-blue-700 border-blue-200", Icon: CalendarDays },
  monthly:   { label: "Mensile",     color: "bg-purple-100 text-purple-700 border-purple-200", Icon: CalendarRange },
  manual:    { label: "Manuale",     color: "bg-emerald-100 text-emerald-700 border-emerald-200", Icon: Hand },
  emergency: { label: "Emergenza",   color: "bg-amber-100 text-amber-700 border-amber-200", Icon: LifeBuoy },
};

export default function BackupRestore() {
  const [info, setInfo] = useState(null);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [validatingFile, setValidatingFile] = useState(null);
  const [statusType, setStatusType] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");
  const fileInputRef = useRef(null);
  const isMounted = useRef(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [info1, list1] = await Promise.all([
        api.getBackupInfo().catch(() => null),
        api.listBackups().catch(() => ({ backups: [] })),
      ]);
      if (!isMounted.current) return;
      setInfo(info1);
      setList(list1.backups || []);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    refresh();
    return () => { isMounted.current = false; };
  }, [refresh]);

  const showStatus = (type, message) => {
    setStatusType(type);
    setStatusMessage(message);
  };

  const handleCreateManual = async () => {
    setCreating(true);
    setStatusType(null);
    try {
      const r = await api.createManualBackup();
      showStatus("success",
        `Backup manuale creato: ${r.filename} (${formatSize(r.size)}, ${r.documents} documenti). Integrità: OK.`);
      await refresh();
    } catch (e) {
      showStatus("error", e.message || "Errore durante la creazione del backup");
    } finally {
      if (isMounted.current) setCreating(false);
    }
  };

  const handleDownloadStream = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    const token = localStorage.getItem("auth_token");
    window.location.href = `${backendUrl}/api/backup/download?token=${encodeURIComponent(token)}`;
    setTimeout(refresh, 2500);
  };

  const handleDownloadFile = (filename) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    const token = localStorage.getItem("auth_token");
    window.location.href = `${backendUrl}/api/backup/download/${encodeURIComponent(filename)}?token=${encodeURIComponent(token)}`;
  };

  const handleValidate = async (filename) => {
    setValidatingFile(filename);
    try {
      const r = await api.validateBackup(filename);
      if (r.valid) {
        showStatus("success",
          `Backup ${filename} integro: ${r.collections} collezioni, creato il ${formatDate(r.meta?.created_at)}.`);
      } else {
        showStatus("error", `Backup CORROTTO: ${r.error}`);
      }
    } catch (e) {
      showStatus("error", e.message || "Errore validazione");
    } finally {
      if (isMounted.current) setValidatingFile(null);
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Eliminare il backup "${filename}"?\nL'operazione non è reversibile.`)) return;
    try {
      await api.deleteBackup(filename);
      showStatus("success", `Backup eliminato: ${filename}`);
      await refresh();
    } catch (e) {
      showStatus("error", e.message || "Errore eliminazione");
    }
  };

  const handleRestoreClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleRestoreFile = async (e) => {
    const files = e.target.files;
    if (!files || !files.length) return;
    const file = files[0];
    if (!file.name.endsWith(".zip")) {
      showStatus("error", "Seleziona un file ZIP di backup valido.");
      return;
    }
    const ok = window.confirm(
      "ATTENZIONE — RIPRISTINO DATABASE\n\n" +
      "Tutti i dati attuali saranno SOSTITUITI con quelli del backup.\n\n" +
      "Il sistema creerà automaticamente un backup di emergenza dello stato attuale " +
      "prima di procedere, così potrai sempre tornare indietro.\n\n" +
      `File: ${file.name} (${formatSize(file.size)})\n\n` +
      "Vuoi procedere?"
    );
    if (!ok) {
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setRestoring(true);
    setStatusType(null);
    try {
      const r = await api.restoreBackup(file);
      const emergency = r.emergencyBackup?.filename
        ? `\nBackup di emergenza salvato: ${r.emergencyBackup.filename}`
        : "";
      showStatus("success",
        `Ripristino completato. ${r.restored?.length || 0} collezioni ripristinate.${emergency}\nRicaricamento...`);
      setTimeout(() => window.location.reload(), 3000);
    } catch (err) {
      showStatus("error", err.message || "Errore durante il ripristino");
      if (isMounted.current) setRestoring(false);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ---------- Render ----------
  const counts = info?.counts || {};
  const totalBackups = list.length;
  const last = info?.lastBackup;

  return (
    <div className="space-y-6" data-testid="backup-restore-section">
      {/* HEADER STATUS DASHBOARD */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-emerald-600" />
                Sistema di backup
                {info?.status === "active" ? (
                  <Badge key="status-active" className="bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid="backup-status-badge">ATTIVO</Badge>
                ) : (
                  <Badge key="status-inactive" className="bg-slate-100 text-slate-600">—</Badge>
                )}
              </CardTitle>
              <CardDescription>
                Snapshot automatici giornalieri + mensili con anti-duplicato, ripristino sicuro.
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={refresh} disabled={loading} data-testid="backup-refresh-btn">
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Aggiorna
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-lg border bg-slate-50 p-3" data-testid="backup-stat-database">
              <p className="text-xs uppercase tracking-wide text-slate-500">Database</p>
              <p className="text-sm font-semibold text-slate-800 mt-1 truncate" title={info?.database?.name}>
                {info?.database?.name || "—"}
              </p>
              <p className="text-[11px] text-slate-500 truncate" title={info?.database?.uri}>
                {info?.database?.uri || "—"}
              </p>
              <p className="text-[11px] text-slate-500 mt-1">
                <strong>{info?.database?.totalDocuments ?? "—"}</strong> documenti totali
              </p>
            </div>

            <div className="rounded-lg border bg-slate-50 p-3" data-testid="backup-stat-counts">
              <p className="text-xs uppercase tracking-wide text-slate-500">Backup disponibili</p>
              <p className="text-2xl font-bold text-slate-800 leading-tight">{totalBackups}</p>
              <p className="text-[11px] text-slate-500 mt-1 space-x-2">
                <span>Giorn: <strong>{counts.daily || 0}</strong></span>
                <span>Mens: <strong>{counts.monthly || 0}</strong></span>
                <span>Manu: <strong>{counts.manual || 0}</strong></span>
              </p>
            </div>

            <div className="rounded-lg border bg-slate-50 p-3" data-testid="backup-stat-storage">
              <p className="text-xs uppercase tracking-wide text-slate-500">Spazio occupato</p>
              <p className="text-sm font-semibold text-slate-800 mt-1">{formatSize(info?.storage?.totalBytes || 0)}</p>
              <p className="text-[11px] text-slate-500 truncate" title={info?.storage?.path}>
                <HardDrive className="inline h-3 w-3 mr-0.5" /> {info?.storage?.path || "—"}
              </p>
            </div>

            <div className="rounded-lg border bg-slate-50 p-3" data-testid="backup-stat-last">
              <p className="text-xs uppercase tracking-wide text-slate-500">Ultimo backup</p>
              {last ? (
                <div key="last-backup-present">
                  <p className="text-sm font-semibold text-slate-800 mt-1">{formatDate(last.createdAt)}</p>
                  <p className="text-[11px] text-slate-500 truncate" title={last.filename}>
                    {KIND_META[last.kind]?.label || last.kind} · {formatSize(last.size)}
                  </p>
                </div>
              ) : (
                <p key="last-backup-none" className="text-sm text-slate-400 mt-1">Nessun backup ancora</p>
              )}
            </div>
          </div>

          {/* Schedule + retention info */}
          {info?.schedule && (
            <div className="mt-4 rounded-md bg-blue-50 border border-blue-100 px-3 py-2 text-xs text-blue-800 space-y-0.5">
              <p>
                <strong>Snapshot automatici:</strong> {info.schedule.daily}; {info.schedule.monthly}.
                I backup automatici vengono <strong>creati solo se il database è cambiato</strong> rispetto allo snapshot precedente.
              </p>
              <p>
                <strong>Retention:</strong> ultimi {info.retention?.daily} giornalieri,
                {" "}{info.retention?.monthly} mensili, {info.retention?.manual} manuali.
                I file più vecchi vengono eliminati automaticamente.
              </p>
            </div>
          )}

          {/* DB changed since last backup indicator.
              IMPORTANT (React 19): use distinct keyed wrappers per branch so
              React mounts/unmounts cleanly when the flag flips, instead of
              trying to reconcile children inside the same <div>/<span>
              (the two branches have very different inner structures). */}
          {info && (
            info.dbChangedSinceLastBackup ? (
              <div
                key="db-changed-yes"
                className="mt-3 rounded-md border px-3 py-2 text-sm flex items-center gap-2 bg-amber-50 border-amber-200 text-amber-800"
                data-testid="backup-db-changed-indicator"
              >
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>
                  <strong>Database modificato dall'ultimo backup: SÌ</strong>
                  {" "}— ci sono cambiamenti non ancora salvati in uno snapshot. Premi
                  {" "}<em>"Crea backup adesso"</em> oppure attendi lo snapshot automatico.
                </span>
              </div>
            ) : (
              <div
                key="db-changed-no"
                className="mt-3 rounded-md border px-3 py-2 text-sm flex items-center gap-2 bg-emerald-50 border-emerald-200 text-emerald-800"
                data-testid="backup-db-changed-indicator"
              >
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                <span>
                  <strong>Database modificato dall'ultimo backup: NO</strong>
                  {" "}— l'ultimo snapshot rispecchia lo stato attuale del database.
                </span>
              </div>
            )
          )}
        </CardContent>
      </Card>

      {/* STATUS TOAST — keyed branches to avoid React 19 reconciliation
          issues when statusType flips between success/error. */}
      {statusType === "success" && (
        <div
          key="status-success"
          className="rounded-md border px-3 py-2 text-sm flex items-start gap-2 bg-emerald-50 border-emerald-200 text-emerald-800"
          data-testid="backup-status-message"
        >
          <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
          <span className="whitespace-pre-line">{statusMessage}</span>
        </div>
      )}
      {statusType === "error" && (
        <div
          key="status-error"
          className="rounded-md border px-3 py-2 text-sm flex items-start gap-2 bg-red-50 border-red-200 text-red-800"
          data-testid="backup-status-message"
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span className="whitespace-pre-line">{statusMessage}</span>
        </div>
      )}

      {/* ACTIONS */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Azioni</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button onClick={handleCreateManual} disabled={creating} className="bg-emerald-600 hover:bg-emerald-700" data-testid="backup-create-manual-btn">
            {/* Both icons always rendered, hidden via CSS — avoids React 19
                in-place <svg> reconciliation crashes when swapping icon type. */}
            <Loader2 className={`h-4 w-4 mr-1 animate-spin ${creating ? '' : 'hidden'}`} />
            <Database className={`h-4 w-4 mr-1 ${creating ? 'hidden' : ''}`} />
            Crea backup adesso
          </Button>
          <Button variant="outline" onClick={handleDownloadStream} data-testid="backup-download-stream-btn">
            <Download className="h-4 w-4 mr-1" /> Scarica backup completo
          </Button>
          <Button variant="outline" onClick={handleRestoreClick} disabled={restoring} className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="backup-restore-btn">
            <Loader2 className={`h-4 w-4 mr-1 animate-spin ${restoring ? '' : 'hidden'}`} />
            <Upload className={`h-4 w-4 mr-1 ${restoring ? 'hidden' : ''}`} />
            Ripristina da file...
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".zip"
            onChange={handleRestoreFile}
            className="hidden"
            data-testid="backup-restore-file-input"
          />
        </CardContent>
      </Card>

      {/* BACKUP LIST */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <HardDrive className="h-4 w-4" />
            Backup disponibili ({totalBackups})
          </CardTitle>
          <CardDescription>Cronologia degli snapshot salvati su questo server.</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Three-way render: keyed branches for safe React 19 mount/unmount */}
          {loading && (
            <div key="backup-list-loading" className="text-center text-sm text-slate-400 py-6">
              <Loader2 className="h-5 w-5 mx-auto animate-spin mb-2" />
              Caricamento…
            </div>
          )}
          {!loading && list.length === 0 && (
            <div key="backup-list-empty" className="text-center text-sm text-slate-400 py-6">
              Nessun backup ancora creato. Usa "Crea backup adesso" per il primo snapshot manuale.
            </div>
          )}
          {!loading && list.length > 0 && (
            <div key="backup-list-table" className="overflow-x-auto -mx-2">
              <table className="w-full text-sm min-w-[640px]" data-testid="backup-list-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b">
                    <th className="px-3 py-2">Nome file</th>
                    <th className="px-3 py-2">Tipo</th>
                    <th className="px-3 py-2">Dimensione</th>
                    <th className="px-3 py-2">Creato</th>
                    <th className="px-3 py-2 text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {list.map((b) => {
                    const meta = KIND_META[b.kind] || KIND_META.manual;
                    const Ic = meta.Icon;
                    return (
                      <tr key={b.filename} className="hover:bg-slate-50" data-testid={`backup-row-${b.filename}`}>
                        <td className="px-3 py-2 font-mono text-xs">{b.filename}</td>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className={`text-[11px] ${meta.color}`}>
                            <Ic className="h-3 w-3 mr-1 inline" /> {meta.label}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-slate-600">{formatSize(b.size)}</td>
                        <td className="px-3 py-2 text-slate-600">{formatDate(b.created)}</td>
                        <td className="px-3 py-2 text-right space-x-1">
                          <Button size="sm" variant="ghost" onClick={() => handleDownloadFile(b.filename)} title="Scarica" data-testid={`backup-download-${b.filename}`}>
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleValidate(b.filename)} disabled={validatingFile === b.filename} title="Verifica integrità" data-testid={`backup-validate-${b.filename}`}>
                            <Loader2 className={`h-4 w-4 animate-spin ${validatingFile === b.filename ? '' : 'hidden'}`} />
                            <ShieldCheck className={`h-4 w-4 ${validatingFile === b.filename ? 'hidden' : ''}`} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(b.filename)} title="Elimina" data-testid={`backup-delete-${b.filename}`} className="text-red-600 hover:bg-red-50">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* FUTURE — Cloud backup placeholder */}
      <Card className="border-dashed">
        <CardContent className="py-4 px-4 text-sm text-slate-500 flex items-center gap-2">
          <Shield className="h-4 w-4 shrink-0" />
          <span>
            <strong>Backup su cloud</strong> (Google Drive, OneDrive, Dropbox) sarà disponibile in una versione futura.
          </span>
        </CardContent>
      </Card>
    </div>
  );
}
